import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, GoldButton } from "@/admin/ui";
import { broadcastSettingsUpdate } from "@/context/SiteConfig";

const GROUPS = [
  { title: "Identidade", fields: [
    ["site_name", "Nome do Site"], ["tagline", "Subtítulo"],
    ["logo", "Logo (URL)"], ["favicon", "Favicon (URL)"], ["copyright", "Copyright"], ["cnpj", "CNPJ"],
  ]},
  { title: "Redes & Contato", fields: [
    ["instagram", "Instagram"], ["telegram", "Telegram"], ["whatsapp", "WhatsApp (link)"], ["email", "E-mail"],
  ]},
  { title: "Menu — Todas as Plataformas", fields: [
    ["all_platforms_label", "Rótulo do botão (menu)"],
    ["all_platforms_url", "URL de acesso (todas as plataformas)"],
  ]},
  { title: "Autenticação & Registro", fields: [
    ["forgot_password_url", "URL 'Esqueceu a senha?'"],
    ["register_terms_summary", "Texto curto acima do checkbox de Termos"],
  ]},
  { title: "Cores & Fontes", fields: [
    ["color_primary", "Cor Primária"], ["color_secondary", "Cor Secundária"],
    ["color_accent", "Cor Destaque"], ["font_heading", "Fonte Títulos"], ["font_body", "Fonte Texto"],
  ]},
  { title: "SEO & Analytics", fields: [
    ["seo_title", "SEO Título"], ["seo_description", "SEO Descrição"], ["google_analytics", "Google Analytics ID"],
  ]},
];

export const SettingsManager = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { key, _id, ...payload } = s;
      await api.put("/admin/settings", payload);
      broadcastSettingsUpdate();
      toast.success("Configurações salvas");
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const changePw = async () => {
    if (!pw.current_password || !pw.new_password) return toast.error("Preencha as senhas");
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Senha alterada");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  if (!s) return <div className="text-white/40">Carregando...</div>;

  return (
    <div data-testid="admin-settings">
      <PageTitle title="Configurações" subtitle="Ajustes globais do site"
        action={<GoldButton onClick={save} disabled={saving} data-testid="settings-save"><Save className="w-4 h-4 inline mr-1" /> {saving ? "Salvando..." : "Salvar"}</GoldButton>} />

      <div className="grid md:grid-cols-2 gap-6">
        {GROUPS.map((grp) => (
          <Card key={grp.title}>
            <h3 className="font-display font-bold text-white mb-4">{grp.title}</h3>
            <div className="space-y-4">
              {grp.fields.map(([k, label]) => (
                <Field key={k} label={label}>
                  <div className="flex gap-2 items-center">
                    <input value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={inputCls} data-testid={`settings-${k}`} />
                    {k.startsWith("color_") && (
                      <input type="color" value={s[k] || "#F5C542"} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="w-10 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer shrink-0" />
                    )}
                  </div>
                </Field>
              ))}
            </div>
          </Card>
        ))}

        <Card>
          <h3 className="font-display font-bold text-white mb-4 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#F5C542]" /> Segurança — Alterar Senha
          </h3>
          <div className="space-y-4">
            <Field label="Senha Atual">
              <input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="pw-current" />
            </Field>
            <Field label="Nova Senha" hint="Mínimo 6 caracteres">
              <input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="pw-new" />
            </Field>
            <GoldButton onClick={changePw} data-testid="pw-change">Atualizar Senha</GoldButton>
          </div>
        </Card>
      </div>
    </div>
  );
};
